import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Radar, ShieldCheck, Wind, Cloud, MapPin } from "lucide-react";
import Ticker from "@/components/Ticker";

const HERO_BG = "https://images.unsplash.com/photo-1598336331578-0fe91c2a0abd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxsaWdodG5pbmclMjBzdG9ybSUyMG5pZ2h0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4MDcxNzgxMnww&ixlib=rb-4.1.0&q=85";
const VEHICLE_IMG = "https://images.unsplash.com/photo-1617072635572-3d081e84ddbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxzdG9ybSUyMGNoYXNlciUyMHZlaGljbGV8ZW58MHx8fHwxNzgwNzE3ODEzfDA&ixlib=rb-4.1.0&q=85";
const RADAR_IMG = "https://images.pexels.com/photos/34332794/pexels-photo-34332794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STORM_TEX = "https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxzZXZlcmUlMjB3ZWF0aGVyJTIwc3Rvcm0lMjBjbG91ZHN8ZW58MHx8fHwxNzgwNzE3ODEzfDA&ixlib=rb-4.1.0&q=85";

const Stat = ({ value, label, suffix = "" }) => (
  <div className="border-l-2 border-yellow-400/70 pl-4">
    <div className="font-heading text-4xl md:text-5xl font-black text-white font-mono tracking-tight">
      {value}<span className="text-yellow-400">{suffix}</span>
    </div>
    <div className="overline mt-2 !text-zinc-500">{label}</div>
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page" className="grain">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <img
          src={HERO_BG}
          alt="Lightning storm"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-transparent to-transparent" />
        <div className="absolute inset-0 pointer-events-none animate-lightning bg-white/5" />

        <div className="container-vg relative z-10 pb-24 pt-32">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="overline !text-red-400">Live Field Ops · 2026 Season</span>
            </div>
            <h1
              data-testid="hero-headline"
              className="font-heading font-black uppercase text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tighter text-white"
            >
              We chase what<br />
              others <span className="text-yellow-400">flee from.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              Vortex Ghost is a field research and storm chasing collective documenting severe convective weather across
              the Great Plains and Dixie Alley. We collect data, capture imagery, and contribute to the science that
              saves lives.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/apply" className="btn-primary" data-testid="hero-apply-btn">
                Join the Chase Team <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/research" className="btn-secondary" data-testid="hero-research-btn">
                View Research
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Ticker />

      {/* STATS */}
      <section className="section-pad">
        <div className="container-vg grid grid-cols-2 md:grid-cols-4 gap-8">
          <Stat value="148" label="Storms Intercepted" />
          <Stat value="12K" label="Data Points Logged" suffix="+" />
          <Stat value="11" label="States Covered" />
          <Stat value="7" label="Years In Field" />
        </div>
      </section>

      {/* MISSION / BENTO */}
      <section className="section-pad bg-[#0d0f12] border-y border-white/5">
        <div className="container-vg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
            <div className="lg:col-span-5">
              <div className="overline mb-4">Our Mission</div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                Science under <span className="text-yellow-400">the meso</span>.
              </h2>
            </div>
            <div className="lg:col-span-7 text-lg text-zinc-400 leading-relaxed">
              We deploy mobile mesonet vehicles, drones, and ground-truth observers ahead of severe storms — gathering
              kinematic and thermodynamic data that conventional radar networks miss. Every chase produces datasets,
              imagery, and warnings that flow back into the meteorology community.
            </div>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="card-vg md:col-span-7 row-span-2 relative overflow-hidden p-0 min-h-[420px]">
              <img src={VEHICLE_IMG} alt="Chase vehicle" className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111418] via-[#111418]/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <div className="overline">Field Operations</div>
                <h3 className="font-heading text-3xl md:text-4xl font-bold mt-2 text-white">Mobile Mesonet Fleet</h3>
                <p className="text-zinc-300 mt-3 max-w-md">Three custom-built vehicles equipped with anemometers, hail pads, slow-motion arrays, and SDR radar.</p>
              </div>
            </div>

            <div className="card-vg md:col-span-5 p-8">
              <Radar className="w-7 h-7 text-yellow-400" />
              <h3 className="font-heading text-2xl font-semibold mt-4">Atmospheric Sensing</h3>
              <p className="text-zinc-400 mt-3">Dual-pol mobile radar and balloon soundings collect inflow data that fixed stations cannot reach.</p>
            </div>

            <div className="card-vg md:col-span-5 p-8">
              <ShieldCheck className="w-7 h-7 text-sky-400" />
              <h3 className="font-heading text-2xl font-semibold mt-4">Safety Protocols</h3>
              <p className="text-zinc-400 mt-3">Every chaser is SKYWARN trained, with ALICE protocols, route discipline, and mandatory bail-out windows.</p>
            </div>

            <div className="card-vg md:col-span-4 p-8">
              <Activity className="w-7 h-7 text-yellow-400" />
              <h3 className="font-heading text-xl font-semibold mt-4">Real-time Reporting</h3>
              <p className="text-zinc-400 mt-3 text-sm">Ground-truth reports relayed to NWS within seconds of confirmation.</p>
            </div>
            <div className="card-vg md:col-span-4 p-8">
              <Cloud className="w-7 h-7 text-sky-400" />
              <h3 className="font-heading text-xl font-semibold mt-4">Open Data</h3>
              <p className="text-zinc-400 mt-3 text-sm">Datasets released to universities and the public after each chase season.</p>
            </div>
            <div className="card-vg md:col-span-4 p-8">
              <Wind className="w-7 h-7 text-yellow-400" />
              <h3 className="font-heading text-xl font-semibold mt-4">Tornado Genesis</h3>
              <p className="text-zinc-400 mt-3 text-sm">Researching the elusive transition from rotating updraft to surface vortex.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="section-pad relative overflow-hidden">
        <img src={STORM_TEX} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        <div className="container-vg relative">
          <div className="overline">Coverage Map</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-3">Where we deploy.</h2>
          <p className="text-zinc-400 mt-4 max-w-2xl">From the High Plains of Colorado down through Dixie Alley, our crews track convection across eleven states.</p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {["Oklahoma", "Texas", "Kansas", "Nebraska", "Iowa", "Missouri", "Arkansas", "Mississippi", "Alabama", "Tennessee", "Colorado", "South Dakota"].map((s) => (
              <div key={s} className="card-vg p-5 flex items-center gap-3" data-testid={`coverage-${s.toLowerCase()}`}>
                <MapPin className="w-4 h-4 text-yellow-400" />
                <span className="font-mono text-sm tracking-wider uppercase text-zinc-300">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad relative overflow-hidden">
        <img src={RADAR_IMG} alt="Radar dome" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        <div className="container-vg relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="overline">Recruiting · 2026</div>
            <h2 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase mt-3">
              Think you have <br /> what it takes?
            </h2>
            <p className="text-zinc-300 mt-5 max-w-xl text-lg">
              We're recruiting drivers, navigators, mesonet operators, and scientific observers for the 2026 spring season.
              Vehicles, equipment, training — all provided.
            </p>
            <div className="flex gap-4 mt-8">
              <Link to="/apply" className="btn-primary" data-testid="cta-apply-btn">Submit Application <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="btn-secondary" data-testid="cta-contact-btn">Contact Ops</Link>
            </div>
          </div>
          <div className="md:col-span-5 glass p-8">
            <div className="overline">Requirements</div>
            <ul className="mt-4 space-y-3 text-zinc-300 text-sm">
              <li className="flex gap-3"><span className="text-yellow-400 font-mono">01</span> Valid driver's license &middot; clean record</li>
              <li className="flex gap-3"><span className="text-yellow-400 font-mono">02</span> Physically fit, calm under pressure</li>
              <li className="flex gap-3"><span className="text-yellow-400 font-mono">03</span> Working knowledge of severe weather</li>
              <li className="flex gap-3"><span className="text-yellow-400 font-mono">04</span> Availability March–July weekends</li>
              <li className="flex gap-3"><span className="text-yellow-400 font-mono">05</span> SKYWARN certification (or willing to obtain)</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
